import { Inject, Injectable, InjectionToken } from '@angular/core';

import { fromEvent, Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { generateUuid } from '@recruitee/utils';
import { WorkerFunctions, WorkerTask, WorkerTaskDone } from '@recruitee/worker';

export const WorkerFactory = new InjectionToken<() => Worker>('Worker factory');

@Injectable({ providedIn: 'root' })
export class WorkerPool {
  private workers: Worker[];
  private lastWorkerId = 0;

  constructor(
    @Inject(WorkerFactory)
    private createWorker: () => Worker,
  ) {}

  public init(poolSize?: number) {
    const hardwareConcurrency = navigator.hardwareConcurrency;
    const workersCount = hardwareConcurrency ? Math.min(hardwareConcurrency, 4) : 2;

    this.workers = [...Array(poolSize || workersCount)].map(() => this.createWorker());
  }

  public fillInReferences(
    ...args: Parameters<WorkerFunctions['fillInReferences']>
  ): Observable<ReturnType<WorkerFunctions['fillInReferences']>> {
    const task = this.createTask('fillInReferences', args);
    return this.exec(task);
  }

  // private static createWorker() {
  //   return new Worker('./app.worker', { type: 'module' });
  // }

  private createTask<T extends keyof WorkerFunctions>(
    fnName: T,
    args: Parameters<WorkerFunctions[T]>,
  ): WorkerTask {
    return {
      id: generateUuid(),
      fnName,
      args,
    };
  }

  private exec(task: WorkerTask) {
    const worker = this.workers[this.lastWorkerId];
    this.lastWorkerId = (this.lastWorkerId + 1) % this.workers.length; // Change round robin to something smarter
    const result = fromEvent<MessageEvent>(worker, 'message').pipe(
      map(event => <WorkerTaskDone>event.data),
      filter(doneTask => doneTask.id === task.id),
      first(),
      map(doneTask => doneTask.result),
    );

    worker.postMessage(task);
    return result;
  }
}
